import { DIR_VECTORS } from './constants';
import type { Direction, Rect, Vec2 } from './types';

let nextId = 1;

export function makeId(prefix: string): string {
  nextId += 1;
  return `${prefix}-${nextId.toString(36)}`;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function approach(value: number, target: number, amount: number): number {
  if (value < target) return Math.min(value + amount, target);
  if (value > target) return Math.max(value - amount, target);
  return value;
}

export function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

export function rectCenter(rect: Rect): Vec2 {
  return {
    x: (rect.left + rect.right) / 2,
    y: (rect.top + rect.bottom) / 2,
  };
}

export function directionFromInput(up: boolean, down: boolean, left: boolean, right: boolean, current: Direction): Direction | null {
  const vertical = up ? 'up' : down ? 'down' : null;
  const horizontal = left ? 'left' : right ? 'right' : null;
  if (vertical && horizontal) {
    if (current === 'left' || current === 'right') return horizontal;
    return vertical;
  }
  return (vertical || horizontal) as Direction | null;
}

export function directionToward(from: Vec2, to: Vec2): Direction {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? 'right' : 'left';
  return dy > 0 ? 'down' : 'up';
}

export function vectorFor(direction: Direction): Vec2 {
  return DIR_VECTORS[direction];
}

export function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

export function pick<T>(items: T[], random = Math.random): T {
  return items[Math.floor(random() * items.length)];
}

export function distance(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
